import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { TranslationKey } from './i18n';
import type { BadgeTone } from '../components/ui';

/**
 * Monitoring (operator, 2026-10-20): our servers, the projects on them, and the
 * URLs checked there — websites, APIs and ports. The Administrator's alone
 * (`monitoring.manage`). A server names the projects it runs; a URL on it may
 * name one of those, or none.
 */

export type ServerType = 'VPS' | 'CLOUD' | 'DEDICATED' | 'OTHER';
export const SERVER_TYPES: ServerType[] = ['VPS', 'CLOUD', 'DEDICATED', 'OTHER'];

/** Where a server stands, from the URLs on it that are being checked. */
export type ServerHealth = 'ONLINE' | 'WARNING' | 'OFFLINE' | 'PENDING' | 'NOT_MONITORED';
export type MonitorStatus = 'PENDING' | 'UP' | 'DEGRADED' | 'DOWN';
export const MONITOR_STATUSES: MonitorStatus[] = ['UP', 'DEGRADED', 'DOWN', 'PENDING'];
export type MonitorKind = 'HTTP' | 'TCP';
export type MonitorMethod = 'GET' | 'HEAD' | 'POST';
export type MonitorSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
export const MONITOR_SEVERITIES: MonitorSeverity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

export interface MonitoringProjectRef {
  id: string;
  name: string;
  nameAr: string | null;
  code: string;
  status: string;
  /** Deleted since it was put on the server: kept on the server, never offered again. */
  deleted?: boolean;
}

export interface UrlCounts {
  total: number;
  up: number;
  degraded: number;
  down: number;
  pending: number;
  paused: number;
}

export interface ServerRow {
  id: string;
  name: string;
  ipAddress: string | null;
  hostname: string | null;
  provider: string | null;
  type: ServerType;
  location: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  projects: MonitoringProjectRef[];
  health: ServerHealth;
  urlCounts: UrlCounts;
}

export interface UrlRow {
  id: string;
  serverId: string;
  projectId: string | null;
  name: string;
  kind: MonitorKind;
  url: string;
  method: MonitorMethod;
  expectedStatus: string | null;
  keyword: string | null;
  intervalSeconds: number | null;
  timeoutSeconds: number | null;
  failuresToConfirm: number | null;
  slowMs: number | null;
  sslWarnDays: number | null;
  severity: MonitorSeverity;
  isPaused: boolean;
  status: MonitorStatus;
  createdAt: string;
  updatedAt: string;
  server?: { id: string; name: string };
  project: MonitoringProjectRef | null;
  // What the monitor keeps on it.
  nextCheckAt: string;
  firstCheckedAt: string | null;
  lastCheckedAt: string | null;
  statusChangedAt: string | null;
  lastStatusCode: number | null;
  lastResponseMs: number | null;
  lastErrorCode: CheckCode | null;
  lastErrorDetail: string | null;
  sslExpiresAt: string | null;
  sslIssuer: string | null;
  /** The list's own: the share of the last 24 hours it was up; null before its first check. */
  uptimeDay?: number | null;
}

/** Why a check failed, or why a URL is Degraded — check-result.ts in the API. */
export type CheckCode =
  | 'TIMEOUT'
  | 'DNS'
  | 'REFUSED'
  | 'RESET'
  | 'UNREACHABLE'
  | 'TLS'
  | 'STATUS'
  | 'KEYWORD'
  | 'REDIRECTS'
  | 'BLOCKED'
  | 'NETWORK'
  | 'SLOW'
  | 'SSL_EXPIRING';

export interface MonitorCheckRow {
  id: string;
  checkedAt: string;
  result: 'UP' | 'DEGRADED' | 'DOWN';
  statusCode: number | null;
  responseMs: number | null;
  errorCode: CheckCode | null;
  errorDetail: string | null;
}

export type IncidentEndReason = 'RECOVERED' | 'CHANGED' | 'PAUSED' | 'REMOVED';

export interface MonitorIncidentRow {
  id: string;
  urlId: string;
  startedAt: string;
  confirmedAt: string;
  endedAt: string | null;
  endReason: IncidentEndReason | null;
  causeCode: CheckCode;
  causeDetail: string | null;
  failedChecks: number;
  durationSeconds: number;
}

export interface ServerDetail extends ServerRow {
  urls: UrlRow[];
}

export interface UrlDetail extends UrlRow {
  server: { id: string; name: string };
  headers: Array<{ name: string; value: string }> | null;
  body: string | null;
  host: string | null;
  port: number | null;
  /** The share of the last day, 7 days and 30 days it was up; null before its first check. */
  uptime: { day: number | null; week: number | null; month: number | null };
  openIncident: MonitorIncidentRow | null;
  incidents: MonitorIncidentRow[];
  checks: MonitorCheckRow[];
}

export interface MonitorStatusInfo {
  running: boolean;
  seenAt: string | null;
  startedAt: string | null;
  host: string | null;
  /** Something outside watches this server: the Cloudflare watchdog read lately, or a ping URL is set. Absent from an API of before it. */
  watchdog?: boolean;
  /** When the Cloudflare watchdog last read this server, and from which Cloudflare data centre. */
  watchdogSeenAt?: string | null;
  watchdogFrom?: string | null;
  serverNow: string;
}

/** Whether the monitor process is running — its heartbeat, every few seconds. */
export function useMonitorStatus() {
  return useQuery({
    queryKey: ['monitoring', 'status'],
    queryFn: () => api.get<MonitorStatusInfo>('/monitoring/status'),
    refetchInterval: 30_000,
  });
}

/** A check's reason in the reader's language: "No answer within 10 s", "Answered 502" … */
export function checkCodeText(
  code: CheckCode | null | undefined,
  detail: string | null | undefined,
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string,
): string {
  if (!code) return '';
  return t(`monitoring.error.${code}` as TranslationKey, { detail: detail ?? '' });
}

/**
 * "99.95%" — two decimals at most, rounded down, so any downtime at all never
 * shows as 100%. A dash before the first check.
 */
export function uptimeText(percent: number | null | undefined): string {
  if (percent === null || percent === undefined) return '—';
  return `${Math.floor(percent * 100) / 100}%`;
}

export function uptimeTone(percent: number | null | undefined): BadgeTone {
  if (percent === null || percent === undefined) return 'neutral';
  if (percent >= 99.9) return 'success';
  if (percent >= 99) return 'warning';
  return 'error';
}

export interface ServerOption {
  id: string;
  name: string;
  projects: MonitoringProjectRef[];
}

export interface MonitoringDefaults {
  intervalSeconds: number;
  timeoutSeconds: number;
  failuresToConfirm: number;
  slowMs: number;
  sslWarnDays: number;
  intervalChoices: number[];
  limits: Record<'timeoutSeconds' | 'failuresToConfirm' | 'slowMs' | 'sslWarnDays', { min: number; max: number }>;
}

export function useMonitoringDefaults() {
  return useQuery({
    queryKey: ['monitoring', 'defaults'],
    queryFn: () => api.get<MonitoringDefaults>('/monitoring/defaults'),
    staleTime: 60_000,
  });
}

export function useServerOptions() {
  return useQuery({
    queryKey: ['monitoring', 'servers', 'options'],
    queryFn: () => api.get<ServerOption[]>('/monitoring/servers/options'),
  });
}

export const healthLabel = (health: ServerHealth) => `monitoring.health.${health}` as TranslationKey;
export const statusLabel = (status: MonitorStatus) => `monitoring.status.${status}` as TranslationKey;
export const severityLabel = (severity: MonitorSeverity) => `monitoring.severity.${severity}` as TranslationKey;
export const kindLabel = (kind: MonitorKind) => `monitoring.kind.${kind}` as TranslationKey;
export const serverTypeLabel = (type: ServerType) => `monitoring.type.${type}` as TranslationKey;

/** One colour per state, across the section: green is up, amber is a warning, red is down. */
export function healthTone(health: ServerHealth): BadgeTone {
  switch (health) {
    case 'ONLINE':
      return 'success';
    case 'WARNING':
      return 'warning';
    case 'OFFLINE':
      return 'error';
    case 'PENDING':
      return 'info';
    default:
      return 'neutral';
  }
}

export function statusTone(status: MonitorStatus, isPaused = false): BadgeTone {
  if (isPaused) return 'neutral';
  switch (status) {
    case 'UP':
      return 'success';
    case 'DEGRADED':
      return 'warning';
    case 'DOWN':
      return 'error';
    default:
      return 'info';
  }
}

export function severityTone(severity: MonitorSeverity): BadgeTone {
  switch (severity) {
    case 'CRITICAL':
      return 'error';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'info';
    default:
      return 'neutral';
  }
}

/**
 * "Every minute" / "Every 5 minutes". Arabic counts 3–10 minutes with the plural
 * and 11 or more with the singular («كل 5 دقائق», «كل 15 دقيقة»), so there are two keys.
 */
export function intervalText(seconds: number, t: (key: TranslationKey, vars?: Record<string, string | number>) => string) {
  const minutes = Math.round(seconds / 60);
  if (minutes === 1) return t('monitoring.url.everyMinute');
  return t(minutes > 10 ? 'monitoring.url.everyMinutesMany' : 'monitoring.url.everyMinutes', { n: minutes });
}

/** A URL's headers as its form edits them: one `Name: value` per line. */
export function headersToText(headers: Array<{ name: string; value: string }> | null | undefined): string {
  return (headers ?? []).map((h) => `${h.name}: ${h.value}`).join('\n');
}

/** And back: a line without a colon is a name with no value, which the API refuses in words. */
export function textToHeaders(text: string): Array<{ name: string; value: string }> {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const colon = line.indexOf(':');
      return colon === -1
        ? { name: line, value: '' }
        : { name: line.slice(0, colon).trim(), value: line.slice(colon + 1).trim() };
    });
}

/** How long an outage lasted: "45 s", "12 min", "3 h 05 min", "2 d 4 h" — in Arabic "45 ث", "12 د", "3 س 05 د", "2 يوم 4 س". */
export function outageText(seconds: number, locale: 'en' | 'ar'): string {
  const s = Math.max(0, Math.round(seconds));
  const ar = locale === 'ar';
  const unit = { s: ar ? 'ث' : 's', m: ar ? 'د' : 'min', h: ar ? 'س' : 'h', d: ar ? 'يوم' : 'd' };
  if (s < 60) return `${s} ${unit.s}`;
  if (s < 3600) return `${Math.floor(s / 60)} ${unit.m}`;
  if (s < 86_400) return `${Math.floor(s / 3600)} ${unit.h} ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')} ${unit.m}`;
  return `${Math.floor(s / 86_400)} ${unit.d} ${Math.floor((s % 86_400) / 3600)} ${unit.h}`;
}

/** Providers the server form suggests; anything else may be typed. */
export const PROVIDER_SUGGESTIONS = [
  'Hetzner',
  'Contabo',
  'DigitalOcean',
  'OVHcloud',
  'Hostinger',
  'Linode',
  'Vultr',
  'AWS',
  'Google Cloud',
  'Microsoft Azure',
  'Oracle Cloud',
];

// ---------------------------------------------------------------------------
// Alerts
// ---------------------------------------------------------------------------

export type AlertChannel = 'EMAIL' | 'TELEGRAM';
export type AlertKind = 'DOWN' | 'RECOVERED' | 'REMINDER' | 'SSL_EXPIRING' | 'TEST' | 'MONITOR_OFFLINE';
export type AlertDeliveryStatus = 'QUEUED' | 'SENT' | 'FAILED' | 'SKIPPED';

export interface AlertRecipient {
  id: string;
  name: string;
  channel: AlertChannel;
  target: string;
  locale: 'en' | 'ar';
  minSeverity: MonitorSeverity;
  serverIds: string[];
  projectIds: string[];
  /** Every URL tagged with a project, a project added later too; projectIds is then empty. */
  allProjects: boolean;
  notifyRecovery: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  servers?: Array<{ id: string; name: string }>;
  projects?: Array<{ id: string; name: string; nameAr: string | null; code: string }>;
  lastAlertAt?: string | null;
}

export interface AlertDeliveryRow {
  id: string;
  channel: AlertChannel;
  target: string;
  kind: AlertKind;
  subject: string;
  status: AlertDeliveryStatus;
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
  createdAt: string;
  recipient: { id: string; name: string } | null;
}

/** Why no recipient hears that a URL is down (`GET /monitoring/coverage`). */
export type CoverageGap = 'NO_RECIPIENTS' | 'IMPORTANCE' | 'SCOPE';

export interface MonitoringCoverage {
  alertsEnabled: boolean;
  watched: number;
  uncovered: Array<{ id: string; name: string; severity: MonitorSeverity; serverName: string; gap: CoverageGap }>;
}

export interface MonitoringSettingsInfo {
  values: Record<string, number | boolean | string>;
  email: { configured: boolean; missing: string[] };
  telegram: { configured: boolean };
}

export interface TelegramStatus {
  configured: boolean;
  bot: { id: string; username: string | null; name: string | null } | null;
  error: string | null;
}

export const deliveryTone = (status: AlertDeliveryStatus): BadgeTone =>
  status === 'SENT' ? 'success' : status === 'FAILED' ? 'error' : status === 'SKIPPED' ? 'warning' : 'info';

export const alertKindTone = (kind: AlertKind): BadgeTone =>
  kind === 'DOWN' ? 'error' : kind === 'RECOVERED' ? 'success' : kind === 'SSL_EXPIRING' || kind === 'MONITOR_OFFLINE' ? 'warning' : kind === 'REMINDER' ? 'brand' : 'neutral';

// ---------------------------------------------------------------------------
// Overview, charts, incidents
// ---------------------------------------------------------------------------

export interface IncidentListRow extends MonitorIncidentRow {
  url: {
    id: string;
    name: string;
    url: string;
    severity: MonitorSeverity;
    server: { id: string; name: string };
    project: MonitoringProjectRef | null;
  };
}

export interface MonitoringOverview {
  servers: Record<ServerHealth, number> & { total: number };
  urls: UrlCounts;
  uptime: { day: number | null; month: number | null };
  openIncidents: IncidentListRow[];
  recentIncidents: IncidentListRow[];
  certificates: Array<{ url: { id: string; name: string }; server: { id: string; name: string }; sslExpiresAt: string; daysLeft: number; warnDays: number }>;
  slowest: Array<{ id: string; name: string; server: { id: string; name: string }; lastResponseMs: number | null; status: MonitorStatus }>;
  serverCards: Array<{ id: string; name: string; health: ServerHealth; urlCounts: UrlCounts; projects: string[] }>;
  serverNow: string;
}

export type SeriesRange = '24h' | '7d' | '30d' | '90d';
export const SERIES_RANGES: SeriesRange[] = ['24h', '7d', '30d', '90d'];

export interface SeriesPoint {
  /** An ISO instant, or YYYY-MM-DD for the daily ranges. */
  at: string;
  checks: number;
  down: number;
  avgMs: number | null;
  maxMs: number | null;
  p95Ms?: number | null;
}

export interface UrlSeries {
  range: SeriesRange;
  bucketMs: number;
  daily: boolean;
  points: SeriesPoint[];
  /** The last 90 days, oldest first: each day's share of good checks; null with no checks. */
  days: Array<{ day: string; checks: number; uptime: number | null }>;
  timezone: string;
}

/** A day's bar: green all day, amber for a little downtime, red for more, grey with no checks. */
export function dayTone(uptime: number | null): 'up' | 'some' | 'down' | 'none' {
  if (uptime === null) return 'none';
  if (uptime >= 99.9) return 'up';
  if (uptime >= 99) return 'some';
  return 'down';
}
